

import React, { useState, useEffect } from 'react';
import { ref, onValue, remove } from 'firebase/database';

import { query, orderByChild, equalTo, update } from 'firebase/database';
import { db } from '../firebase';
import { FaSearch, FaTrash, FaUser, FaEnvelope, FaPhone, FaShoppingCart, FaTimes, FaUserShield } from 'react-icons/fa';
import styled from 'styled-components';

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 300px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  padding: 0.5rem;
`;

const UsersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #495057;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  vertical-align: middle;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  margin-right: 0.5rem;
  color: ${props => props.delete ? '#e74a3b' : props.admin ? '#6f42c1' : '#36b9cc'};
  &:hover {
    background: rgba(0,0,0,0.05);
  }
`;

const AdminBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.5rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: #f3e8ff;
  color: #6f42c1;
`;

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingOrdersForUser, setViewingOrdersForUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    const usersRef = ref(db, 'Watch/customers');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const usersData = snapshot.val();
      if (usersData) {
        const usersArray = Object.entries(usersData).map(([key, value]) => ({
          id: key,
          mobile: key, // Assuming the mobile number is the key
          ...value
        }));
        setUsers(usersArray);
      } else {
        setUsers([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (viewingOrdersForUser && viewingOrdersForUser.mobile) {
      fetchUserOrders(viewingOrdersForUser.mobile);
    }
  }, [viewingOrdersForUser]);

  const fetchUserOrders = async (userMobile) => {
    setOrdersLoading(true);
    try {
      const ordersRef = ref(db, `Watch/customers/${userMobile}/orders`);
      
      onValue(ordersRef, (snapshot) => {
        const ordersData = snapshot.val();
        if (ordersData) {
          const ordersArray = Object.entries(ordersData).map(([key, value]) => ({
            id: key,
            ...value
          }));
          setUserOrders(ordersArray);
        } else {
          setUserOrders([]);
        }
        setOrdersLoading(false);
      });
    } catch (error) {
      console.error('Error fetching user orders:', error);
      setOrdersLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      try {
        await remove(ref(db, `Watch/customers/${userId}`));
        alert('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
      }
    }
  };

  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      await update(ref(db, `Watch/customers/${userId}`), {
        isAdmin: !currentStatus
      });
    } catch (error) {
      console.error('Error updating admin status:', error);
      alert('Failed to update admin status');
    }
  };

  const handleViewOrders = (user) => {
    setViewingOrdersForUser(user);
  };

  const closeOrdersModal = () => {
    setViewingOrdersForUser(null);
    setUserOrders([]);
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.id.toLowerCase().includes(searchLower) ||
      (user.username && user.username.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.phone && user.phone.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <Container>
        <p>Loading users...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>User Management</Title>
        <SearchContainer>
          <FaSearch style={{ color: '#6c757d' }} />
          <SearchInput
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
      </Header>

      {/* User Orders Modal */}
    {viewingOrdersForUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '800px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3>Orders for {viewingOrdersForUser.username || viewingOrdersForUser.mobile}</h3>
              <button onClick={closeOrdersModal}>
                <FaTimes />
              </button>
            </div>
            
            {ordersLoading ? (
              <p>Loading orders...</p>
            ) : userOrders.length > 0 ? (
              <div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Order ID</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Total</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userOrders.map(order => (
                      <tr key={order.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '0.75rem' }}>{order.id}</td>
                        <td style={{ padding: '0.75rem' }}>
                          {order.date ? new Date(order.date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {order.total ? `₹${order.total.toFixed(2)}` : '$0.00'}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {order.status || 'Pending'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No orders found for this user.</p>
            )}
          </div>
        </div>
      )}

      <UsersTable>
        <TableHeader>
          <tr>
            <TableHeaderCell>User</TableHeaderCell>
            <TableHeaderCell>Contact</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </tr>
        </TableHeader>
        <tbody>
          {filteredUsers.map(user => (
            <TableRow key={user.id}>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4e73df'
                  }}>
                    <FaUser />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600' }}>{user.username || user.mobile}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>Mobile: {user.mobile}</div>
                    {user.isAdmin && (
                      <AdminBadge>
                        <FaUserShield /> Admin
                      </AdminBadge>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {user.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FaEnvelope style={{ color: '#6c757d' }} />
                      <span>{user.email}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaPhone style={{ color: '#6c757d' }} />
                    <span>{user.mobile}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex' }}>
                  <ActionButton 
                    title="View orders"
                    onClick={() => setViewingOrdersForUser(user)}
                  >
                    <FaShoppingCart />
                  </ActionButton>
                  <ActionButton 
                    delete 
                    title="Delete user"
                    onClick={() => handleDeleteUser(user.id)}
                  >
                    <FaTrash />
                  </ActionButton>
        
                </div>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </UsersTable>
    </Container>
  );
};

export default UserManagementPage;